import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useLang from "Shared/hooks/useLanguage";
import MuiSkeleton from "../MuiSkeleton";
import { assignFormValues, clearSignature } from "Utils/Auth";
import { getToxTestingOption } from "Utils/Common/Requisition";

const Checkbox = (props: any) => {
  const { t } = useLang();
  const location = useLocation();
  const [checked, setChecked] = useState<any>(
    location?.state?.reqId ? props.checked : false
  );
  function FindIndex(arr: any[], rid: any) {
    return arr.findIndex((i: any) => i.reqId === rid);
  }
  useEffect(() => {
    setChecked(location?.state?.reqId ? props.checked : false);
  }, [props?.apiCallCondition]);

  useEffect(() => {
    if (!location?.state?.reqId) return;

    if (props.systemFieldName === "NoMedication") {
      if (props?.defaultValue === "True") props.setNoActiveMedication(true);
    }
    assignFormValues(
      props.Inputs,
      props.index,
      props.depControlIndex,
      props.fieldIndex,
      props.defaultValue,
      props.isDependency,
      props.repeatFieldSection,
      props.isDependencyRepeatFields,
      props.repeatFieldIndex,
      props.repeatDependencySectionIndex,
      props.repeatDepFieldIndex,
      props.label,
      props?.setInputs,
      location?.state?.reqId,
      props?.patientId
    );
  }, [props?.defaultValue]);

  const SplitStringByDollarSign = (inputString: any) => {
    const splitIndex = inputString.indexOf("$");
    if (splitIndex === -1) {
      return <span>{inputString}</span>;
    } else {
      const part1 = inputString.substring(0, splitIndex);
      const part2 = inputString.substring(splitIndex + 1);
      return (
        <>
          <span>{part1}</span> <br />
          <span className="text-muted">{part2}</span>
        </>
      );
    }
  };

  // Handles clearing provider validation fields, for both normal fields and repeatFields
  const clearSectionProviderValidation = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { Inputs, index, fieldIndex, providerInfoValidation, setProviderInfoValidation, setInputs, setDisableSSN } = props;
    const field = Inputs[index].fields[fieldIndex];

    // ---- IsNoProvider for Section 30 ----
    if (field.systemFieldName === "IsNoProvider" && Inputs[index].sectionId === 30) {
      const checked = event.target.checked;
      let validationBackupTemp: any[] = [];

      // --- Simple fields ---
      Inputs[index].fields.forEach((fld: any) => {
        if (fld.systemFieldName === "IsNoProvider") return;

        if (checked) {
          // Set overlay visual, backup validation, clear value & signature
          if (!fld.displayType?.includes("overlay")) fld.displayType += " overlay";
          validationBackupTemp.push({ systemFieldName: fld.systemFieldName, validationExpression: fld.validationExpression });
          fld.validationExpression = "";
          fld.defaultValue = "";
          fld.signatureText = "";

          setProviderInfoValidation((prev: any) => [...prev, ...validationBackupTemp]);
          clearSignature();
        } else {
          // Remove overlay on uncheck, restore validation if exists
          fld.displayType = fld.displayType.replace(/\s*overlay\s*/, " ").trim();
          const backup = providerInfoValidation.find((b: any) => b.systemFieldName === fld.systemFieldName);
          if (backup?.validationExpression) {
            fld.validationExpression = backup.validationExpression;
          }
        }
      });

      // --- dependencyControls (simple fields in dependencies) ---
      Inputs[index].dependencyControls.forEach((ctrl: any) => {
        ctrl.dependecyFields.forEach((df: any) => {
          if (checked) {
            // Hide, backup/clear validation, clear values & signature
            if (!df.displayType?.includes("d-none")) df.displayType += " d-none";
            validationBackupTemp.push({ systemFieldName: df.systemFieldName, validationExpression: df.validationExpression });
            df.validationExpression = "";
            df.defaultValue = "";
            df.signatureText = "";

            setProviderInfoValidation((prev: any) => [...prev, ...validationBackupTemp]);
          } else {
            // Restore validations if available
            const backup = providerInfoValidation.find((b: any) => b.systemFieldName === df.systemFieldName);
            if (backup?.validationExpression) {
              df.validationExpression = backup.validationExpression;
            }
          }
        });
      });

      // --- repeatFields ---
      Inputs[index].fields.forEach((fld: any) => {
        if (fld.systemFieldName === "IsNoProvider") return;

        fld.repeatFields?.forEach((rf: any) => {
          if (checked) {
            // Clear values, hide, set overlay, backup/clear validation
            rf.defaultValue = "";
            rf.signatureText = "";
            rf.options.forEach((option: any) => (option.isSelectedDefault = false));

            if (rf.systemFieldName !== "ExistingOrNewProvider") {
              if (!rf.displayType?.includes("d-none")) rf.displayType += " d-none";
            }
            if (!rf.displayType?.includes("overlay")) rf.displayType += " overlay";
            validationBackupTemp.push({ systemFieldName: rf.systemFieldName, validationExpression: rf.validationExpression });
            rf.validationExpression = "";

            setProviderInfoValidation((prev: any) => [...prev, ...validationBackupTemp]);
          } else {
            // Remove overlay, restore validations
            rf.displayType = rf.displayType.replace(" overlay", "");
            const backup = providerInfoValidation.find((b: any) => b.systemFieldName === rf.systemFieldName);
            if (backup?.validationExpression) {
              rf.validationExpression = backup.validationExpression;
            }
          }
        });

        // --- repeatDependencyControls ---
        fld.repeatDependencyControls?.forEach((rdc: any) => {
          rdc.dependecyFields.forEach((df: any) => {
            if (checked) {
              df.defaultValue = "";
              df.signatureText = "";
              if (!df.displayType?.includes("d-none")) df.displayType += " d-none";
              validationBackupTemp.push({ systemFieldName: df.systemFieldName, validationExpression: df.validationExpression });
              df.validationExpression = "";
              setProviderInfoValidation((prev: any) => [...prev, ...validationBackupTemp]);
            } else {
              df.displayType = df.displayType.replace(" overlay", "");
              const backup = providerInfoValidation.find((b: any) => b.systemFieldName === df.systemFieldName);
              if (backup?.validationExpression) {
                df.validationExpression = backup.validationExpression;
              }
            }
          });
        });

        // On uncheck also remove overlay for field itself and restore validation
        if (!checked) {
          fld.displayType = fld.displayType.replace(" overlay", "");
          const backup = providerInfoValidation.find((b: any) => b.systemFieldName === fld.systemFieldName);
          if (backup?.validationExpression) {
            fld.validationExpression = backup.validationExpression;
          }
        }
      });
      setInputs(Inputs);
    }

    // ---- DeclineToProvide: disables SSN if checked ----
    if (field.systemFieldName === "DeclineToProvide") {
      Inputs[index].fields.forEach((fld: any) => {
        if (fld.systemFieldName !== "IsNoProvider") {
          if (event.target.checked) {
            Inputs[index].fields.forEach((inner: any) => {
              if (inner.systemFieldName === "SocialSecurityNumber") {
                inner.defaultValue = "";
                setDisableSSN && setDisableSSN(true);
              }
            });
          } else {
            setDisableSSN && setDisableSSN(false);
          }
        }
      });
      setInputs(Inputs);
    }
  };

  //DeclineToProvide
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props.systemFieldName === "NoSecondaryInsurance") {
      if (event.target.checked) {
        props.onChange(event);
        props.setCheckbox && props.setCheckbox(true);
        props.setIns(true);
      } else {
        props.onChange(event);
        props.setCheckbox && props.setCheckbox(false);
        props.setIns(false);
      }
    }
    if (props.systemFieldName === "NoMedication") {
      props.onChange(event);
      const isChecked = event.target.checked;
      props.setNoActiveMedication(isChecked);
      const MedicalNecessityPGX = props.Inputs.findIndex(
        (item: any) => item.sectionId === 89
      );
      const fields = props.Inputs[props.index].fields;
      if (isChecked) {
        if (MedicalNecessityPGX != -1) {
          props.Inputs[MedicalNecessityPGX].fields.forEach((field: any) => {
            if (field.systemFieldName !== "MedicalNessityPanel") {
              field.defaultValue = "";
              if (field.options) {
                field.options.forEach((option: any) => {
                  option.isSelectedDefault = false;
                });
              }
            }
            if (field.systemFieldName === "MedicalNessityPanel") {
              field.defaultValue = [
                {
                  label: "NoMedication",
                  value: true,
                },
              ];
            }
          });
        }
        // 1️⃣ Reset all fields except "NoMedication"
        fields.forEach((field: any) => {
          if (field.systemFieldName === "NoMedication") return;

          switch (field.systemFieldName) {
            case "Medication":
              field.defaultValue = "";
              field.options?.forEach(
                (option: any) => (option.isSelectedDefault = false)
              );
              break;

            case "AssignedMedications":
              if (Array.isArray(field.medicationList)) {
                field.medicationList.forEach(
                  (med: any) => (med.isSelected = false)
                );
                field.defaultValue = [];
              }
              break;

            case "OtherMedication":
              field.defaultValue = [];
              field.medicationList = [];
              break;
            case "PhotoForPrescribedMedication":
              field.defaultValue = "";
              break;
            case "MedicationPanel":
              field.defaultValue = [
                {
                  name: event.target.name,
                  value: isChecked,
                },
              ];
              field.enableRule = "";
              break;
            case "RepeatStart":
              field.defaultValue = "";
              field.repeatFields?.forEach((rf: any) => {
                if (rf.systemFieldName !== "RepeatStart") rf.defaultValue = "";
              });
              break;

            default:
              break;
          }
        });

        const seenRepeatStart = new Set();
        props.Inputs[props.index].fields = fields.filter((f: any) => {
          if (f.systemFieldName !== "RepeatStart") return true;

          if (!seenRepeatStart.has("RepeatStart")) {
            seenRepeatStart.add("RepeatStart");
            return true;
          }

          return false;
        });
      } else {
        // If unchecked → reset MedicationPanel only
        fields.forEach((field: any) => {
          if (field.systemFieldName === "MedicationPanel") {
            field.defaultValue = [];
          }
        });
      }
    } else {
      props.onChange(event);
      setChecked(event.target.checked);
      clearSectionProviderValidation(event);
    }
  };
  const parseFieldConfiguration = () => {
    if (!props?.fieldConfiguration) return [];
    if (Array.isArray(props.fieldConfiguration))
      return props.fieldConfiguration;
    if (typeof props.fieldConfiguration === "string") {
      try {
        const parsed = JSON.parse(props.fieldConfiguration);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const handleChangeforReflexToConfirmation = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    props.onChange(event);
    if (event.target.checked) {
      const configList = parseFieldConfiguration();
      if (!Array.isArray(configList) || configList.length === 0) return;
      if (!Array.isArray(props?.Inputs) || props?.index === undefined) return;

      const toxSectionIndex = getToxTestingOption(props.Inputs);
      if (toxSectionIndex < 0) return;

      if (!Array.isArray(props?.Inputs?.[toxSectionIndex]?.fields)) return;

      configList.forEach((config: any) => {
        const dependentControlName = config?.SelectableDependentControl;
        const selectableValues = Array.isArray(config?.SelectableValue)
          ? config.SelectableValue
          : [];

        if (!dependentControlName || selectableValues.length === 0) return;

        const targetField = props?.Inputs?.[toxSectionIndex]?.fields.find(
          (field: any) => field?.systemFieldName === dependentControlName
        );
        if (!targetField || !Array.isArray(targetField?.options)) return;

        const selectableIds = new Set(
          selectableValues
            .filter((item: any) => item?.IsSelect)
            .map((item: any) => String(item?.value))
        );
        targetField.options = targetField.options.map((option: any) => {
          const optionId = String(option?.id);
          if (selectableIds.has(optionId)) {
            return {
              ...option,
              isSelectedDefault: true,
            };
          }
          return option;
        });
      });
    }
  };

  const inputElement = useRef<any>(null);

  useEffect(() => {
    if (inputElement.current && props.error) {
      inputElement.current.focus();
    }
  }, [props.error]);

  const isNoProvider =
    props?.Inputs?.[props.index]?.fields[props?.fieldIndex]?.systemFieldName ===
      "IsNoProvider" &&
    props?.Inputs?.[props.index]?.fields[props?.fieldIndex]?.defaultValue ===
      "True";

  useEffect(() => {
    if (isNoProvider && props.Inputs[props.index].sectionId === 30) {
      setChecked(props.defaultValue);

      props.Inputs[props.index].fields.forEach((item: any) => {
        if (item.systemFieldName !== "IsNoProvider") {
          item.displayType += " overlay";
        }
      });
    }

    if (
      props?.Inputs &&
      Array.isArray(props?.Inputs) &&
      props?.Inputs[props?.index] &&
      Array.isArray(props?.Inputs[props?.index]?.fields) &&
      props?.Inputs[props?.index]?.fields[props?.fieldIndex] &&
      props?.Inputs[props?.index]?.fields[props?.fieldIndex]
        ?.systemFieldName === "DeclineToProvide"
    ) {
      if (props?.defaultValue === true || props?.defaultValue === "True") {
        props?.setDisableSSN(true);
      }
    }

    if (
      props?.Inputs &&
      Array.isArray(props?.Inputs) &&
      props?.Inputs[props?.index] &&
      Array.isArray(props?.Inputs[props?.index]?.fields) &&
      props?.Inputs[props?.index]?.fields[props?.fieldIndex] &&
      props?.Inputs[props?.index]?.fields[props?.fieldIndex]
        ?.systemFieldName === "NoKnownFamilyHistory"
    ) {
      if (
        props?.Inputs[props?.index]?.fields[props?.fieldIndex]?.defaultValue
      ) {
        props.setNoFamilyHistory(true);
      }
    }
  }, [props.defaultValue]);
  useEffect(() => {
    if (props.formState) {
      if (
        props.formState.BillingType === "Client Bill" ||
        props.formState.BillingType === "Self Pay"
      ) {
        if (props?.repeatControlLength == 1) {
          props.setIns(true);
          props.setShowButton(false);
        }
      } else {
        if (props?.repeatControlLength == 1) {
          props.setShowButton(true);
          props.setDisableCheckbox(false);
          props.setCheckbox(false);
        }
      }
    }
  }, [props?.formState]);
  const divElement = useRef<HTMLDivElement | null>(null); // Initialize ref for div
  useEffect(() => {
    // Scroll to the div if props.error is present
    if (props.error && divElement.current) {
      divElement.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    props.setErrorFocussedInput && props.setErrorFocussedInput();
  }, [props?.errorFocussedInput]);

  return (
    <>
      <div
        className={
          props?.parentDivClassName
            ? `${props?.parentDivClassName} mb-4`
            : "col-lg-6 col-md-6 col-sm-12 mb-4"
        }
      >
        <div className="form__group form__group--checkbox">
          <label
            className={
              props?.labelClassName
                ? `${props?.labelClassName} fw-500 ` + `${props?.required}`
                : "form-check form-check-inline form-check-solid m-0 fw-500" +
                  `${props?.required}`
            }
          >
            {props.sectionId === 12 ? (
              <input
                className="form-check-input h-20px w-20px"
                type="checkbox"
                name={props.label}
                id={props.label.split(" ").join("")}
                value={props.value}
                onChange={props.onChange}
                checked={props.checked}
                ref={inputElement}
              />
            ) : props.sectionId === 53 ? (
              <input
                className="form-check-input h-20px w-20px"
                type="checkbox"
                id={props.label.split(" ").join("")}
                name={props.label}
                onChange={props.onChange}
                defaultChecked={props.defaultValue}
                checked={props.checked}
                disabled={props?.isEnable}
                ref={inputElement}
              />
            ) : props.sectionId === 17 || props.sectionId === 21 ? (
              <>
                {" "}
                <input
                  className="form-check-input h-20px w-20px"
                  type="checkbox"
                  id={props.label.split(" ").join("")}
                  name={props.label}
                  value={props.value}
                  onChange={props.onChange}
                  defaultChecked={props.defaultValue === "True" ? true : false}
                  checked={props.checked}
                  disabled={props?.disabled}
                  ref={inputElement}
                />
              </>
            ) : props.systemFieldName === "NoSecondaryInsurance" &&
              props.showButton ? (
              <>
                <input
                  className="form-check-input h-20px w-20px"
                  type="checkbox"
                  id={props.label.split(" ").join("")}
                  name={props?.label}
                  value={props?.value}
                  onChange={handleCheckboxChange}
                  checked={props?.defaultValue}
                  defaultChecked={props?.checked}
                  disabled={props?.disableCheckbox}
                  ref={inputElement}
                />
              </>
            ) : props.sectionId === 14 ? (
              <>
                <input
                  className="form-check-input h-20px w-20px"
                  type="checkbox"
                  id={props.label.split(" ").join("")}
                  name={props?.label}
                  value={props?.value}
                  onChange={props?.onChange}
                  checked={props?.defaultValue}
                  defaultChecked={props?.checked}
                  disabled={props?.disabled}
                  ref={inputElement}
                />
              </>
            ) : props.systemFieldName === "VMA" ? (
              <>
                <input
                  className="form-check-input h-20px w-20px"
                  type="checkbox"
                  id={props.label.split(" ").join("")}
                  name={props.label}
                  value={props.value}
                  checked={
                    props.defaultValue === "True" ||
                    props.value === "True" ||
                    props.defaultValue === true ||
                    props.value === true
                  }
                  disabled={true}
                  ref={inputElement}
                />
              </>
            ) : props.sectionId === 89 ? (
              <>
                <input
                  className="form-check-input h-20px w-20px"
                  type="checkbox"
                  id={props.label.split(" ").join("")}
                  name={props.label}
                  value={props.value}
                  onChange={handleCheckboxChange}
                  checked={
                    props.defaultValue === "True" ||
                    props.value === "True" ||
                    props.defaultValue === true ||
                    props.value === true
                  }
                  // disabled={props.noActiveMedication}
                  ref={inputElement}
                />
              </>
            ) : props.sectionId === 35 ? (
              <>
                <input
                  className="form-check-input h-20px w-20px"
                  type="checkbox"
                  id={props.label.split(" ").join("")}
                  name={props.label}
                  value={props.value}
                  onChange={handleChangeforReflexToConfirmation}
                  checked={
                    props.defaultValue === "True" ||
                    props.value === "True" ||
                    props.defaultValue === true ||
                    props.value === true
                  }
                  ref={inputElement}
                />
              </>
            ) : (
              <>
                <input
                  className="form-check-input h-20px w-20px"
                  type="checkbox"
                  id={props.label.split(" ").join("")}
                  name={props.label}
                  value={props.value}
                  onChange={handleCheckboxChange}
                  checked={
                    props.defaultValue === "True" ||
                    props.value === "True" ||
                    props.defaultValue === true ||
                    props.value === true
                  }
                  disabled={props?.disabled || isNoProvider}
                  ref={inputElement}
                />
              </>
            )}

            {props?.loading ? (
              <MuiSkeleton height={22} />
            ) : props.systemFieldName === "Paternal" ||
              props.systemFieldName === "Maternal" ? null : (
              <span
                className={
                  props?.spanClassName
                    ? `${props.spanClassName} text-break fw-400${
                        props.required ? " required" : ""
                      }`
                    : ""
                }
              >
                {props?.testCode ? `${props.testCode}:` : ""}
                {SplitStringByDollarSign(t(props.label))}
              </span>
            )}
          </label>
          {props.error && (
            <div className="form__error">
              <span>{t(props.error)}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Checkbox;
